import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import type {
  CreateQuotaReq,
  CreateQuotaResp,
  GetResourcesResp,
} from '@/api/types';
import QuotaForm, {
  type QuotaFormValues,
} from '@/components/resources/QuotaForm';
import { extractApiMessage } from '@/components/resources/form/errors';

const INITIAL: QuotaFormValues = {
  name: '',
  description: '',
  selectorObject: 'User',
  selectorOperator: 0,
  selectorRegex: '.*',
  resource: '',
  quota: '',
};

export default function CreateQuotaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<QuotaFormValues>(INITIAL);

  const { data: resourcesData, isLoading: resourcesLoading } = useQuery({
    queryKey: ['resources'],
    queryFn: () => api<GetResourcesResp>('/v1/resources'),
  });

  const createMutation = useMutation({
    mutationFn: (req: CreateQuotaReq) =>
      api<CreateQuotaResp>('/v1/quotas', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotas'] });
      navigate('/quotas');
    },
  });

  const submitError = createMutation.error
    ? extractApiMessage(createMutation.error)
    : null;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/quotas"
            className="hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Quotas
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground">New</span>
        </nav>
        <h1 className="text-2xl font-semibold">Create Quota</h1>
        <p className="text-sm text-muted-foreground">
          Quotas layer a limit on top of a resource's global cap. Pick whose
          requests they apply to and the maximum each (or all) of them can
          consume.
        </p>
      </div>

      <QuotaForm
        mode="create"
        values={values}
        onChange={setValues}
        resources={resourcesData?.resources ?? []}
        resourcesLoading={resourcesLoading}
        onSubmit={(req) => createMutation.mutate(req)}
        submitting={createMutation.isPending}
        submitError={submitError}
        cancelHref="/quotas"
      />
    </div>
  );
}
